"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { BASE_URL, CDN_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const BannersContext = createContext();
export const useBanners = () => useContext(BannersContext);

export const BannersProvider = ({ children }) => {
  const { token, adminId, isReady, logout } = useAuth();

  const [banners, setBanners] = useState([]);
  const [bannersLoading, setBannersLoading] = useState(false);

  const mountedRef = useRef(true);

  const BANNERS_ENDPOINT = "/banners";
  const baseURL = BASE_URL.replace(/\/$/, "");

  /* ---------------- Mount Safety ---------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSeBanners = (value) => {
    if (mountedRef.current) setBanners(value);
  };

  /* ---------------- FETCH ---------------- */
  const fetchBanners = useCallback(async () => {
    if (!token) return;
    setBannersLoading(true);
    try {
      const res = await fetch(`${baseURL}${BANNERS_ENDPOINT}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.message || "Fetch failed");
      safeSeBanners(payload?.data || []);
      return payload;
    } catch (err) {
      console.error("[Banners] fetch error:", err);
      if (err.message?.toLowerCase().includes("token")) logout();
      safeSeBanners([]);
    } finally {
      if (mountedRef.current) setBannersLoading(false);
    }
  }, [token, baseURL, logout]);

  /* ---------------- CREATE ---------------- */
  const addBanner = useCallback(
    async (formData) => {
      try {
        // Banner upload uses multipart/form-data (file upload)
        // formData should already be a FormData instance with:
        //   banner (File), status, create_audit_id
        formData.append("create_audit_id", adminId);

        const res = await fetch(`${baseURL}${BANNERS_ENDPOINT}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            // Do NOT set Content-Type — browser sets it with boundary for FormData
          },
          body: formData,
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Create failed");

        await fetchBanners();
        return payload;
      } catch (err) {
        console.error("[Banners] add error:", err);
        throw err;
      }
    },
    [token, adminId, fetchBanners]
  );

  /* ---------------- UPDATE ---------------- */
  const updateBanner = useCallback(
    async (id, formData) => {
      try {
        // formData: FormData instance with banner (File, optional), status, update_audit_id
        formData.append("update_audit_id", adminId);

        const res = await fetch(`${baseURL}${BANNERS_ENDPOINT}/${id}`, {
          method: "POST", // many file-update APIs use POST with _method override or PUT — adjust if needed
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Update failed");

        await fetchBanners();
        return payload;
      } catch (err) {
        console.error("[Banners] update error:", err);
        throw err;
      }
    },
    [token, adminId, fetchBanners]
  );

  /* ---------------- DELETE ---------------- */
  const deleteBanner = useCallback(
    async (id) => {
      try {
        const res = await fetch(`${baseURL}${BANNERS_ENDPOINT}/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ delete_audit_id: adminId }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Delete failed");

        safeSeBanners((prev) =>
          prev.filter((b) => String(b.id) !== String(id))
        );

        return payload;
      } catch (err) {
        console.error("[Banners] delete error:", err);
        throw err;
      }
    },
    [token, adminId]
  );

  /* ---------------- STATUS ---------------- */
  const updateBannerStatus = useCallback(
    async (id, status) => {
      try {
        const res = await fetch(`${baseURL}${BANNERS_ENDPOINT}/${id}/status`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status, update_audit_id: adminId }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Status update failed");

        safeSeBanners((prev) =>
          prev.map((b) =>
            String(b.id) === String(id) ? { ...b, status } : b
          )
        );

        return payload;
      } catch (err) {
        console.error("[Banners] status error:", err);
        throw err;
      }
    },
    [token, adminId]
  );

  /* ---------------- INIT LOAD ---------------- */
  useEffect(() => {
    if (!isReady || !token) return;
    fetchBanners().catch(() => {});
  }, [isReady, token, fetchBanners]);

  return (
    <BannersContext.Provider
      value={{
        banners,
        bannersLoading,
        fetchBanners,
        addBanner,
        updateBanner,
        deleteBanner,
        updateBannerStatus,
       
   getBannerImageUrl: (filename) =>
  filename ? `${CDN_URL}/${filename}` : null,
      }}
    >
      {children}
    </BannersContext.Provider>
  );
};